/**
 * Green Stay Savings Feedback Component
 * Phase P2.2-10: Sustainability Engine v1
 *
 * Displays estimated resource savings after eco-action selection.
 * Provides positive reinforcement to guests choosing sustainability.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Wind, Zap, Leaf } from 'lucide-react';
import type { EcoSavings } from '@/services/greenStay';

interface GreenStaySavingsFeedbackProps {
  savings: EcoSavings;
  visible: boolean;
  onClose?: () => void;
}

export function GreenStaySavingsFeedback({ savings, visible, onClose }: GreenStaySavingsFeedbackProps) {
  const hasSavings = savings.water > 0 || savings.laundry > 0 || savings.energy > 0;

  if (!hasSavings) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50"
        >
          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-xl border border-green-500/30 rounded-2xl p-6 shadow-2xl max-w-md">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">You helped save:</h3>
                <p className="text-green-300 text-sm">Thank you for choosing sustainability</p>
              </div>
            </div>

            {/* Savings Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Water */}
              {savings.water > 0 && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <Droplets className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-white font-medium text-sm truncate">{savings.water}L</div>
                    <div className="text-blue-300 text-xs">Water</div>
                  </div>
                </div>
              )}

              {/* Laundry */}
              {savings.laundry > 0 && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <span className="text-xl flex-shrink-0">🧺</span>
                  <div className="min-w-0">
                    <div className="text-white font-medium text-sm truncate">{savings.laundry}kg</div>
                    <div className="text-purple-300 text-xs">Laundry</div>
                  </div>
                </div>
              )}

              {/* Energy */}
              {savings.energy > 0 && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <Zap className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-white font-medium text-sm truncate">{savings.energy}kWh</div>
                    <div className="text-yellow-300 text-xs">Energy</div>
                  </div>
                </div>
              )}

              {/* CO2 */}
              {savings.co2 > 0 && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Wind className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-white font-medium text-sm truncate">{savings.co2}kg</div>
                    <div className="text-green-300 text-xs">CO₂</div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Message */}
            <div className="text-center text-sm text-green-200/80 pt-3 border-t border-green-500/20">
              Every eco-friendly choice makes a difference
            </div>

            {/* Close Button (optional) */}
            {onClose && (
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <span className="text-white text-xs">✕</span>
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// =============================================================================
// INLINE SAVINGS DISPLAY (for GreenStayPreferences component)
// =============================================================================

interface InlineSavingsDisplayProps {
  savings: EcoSavings;
  compact?: boolean;
}

export function InlineSavingsDisplay({ savings, compact = false }: InlineSavingsDisplayProps) {
  const hasSavings = savings.water > 0 || savings.laundry > 0 || savings.energy > 0;

  if (!hasSavings) return null;

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-xs text-green-300">
        <Leaf className="w-3 h-3" />
        <span>
          {savings.water > 0 && `${savings.water}L water`}
          {savings.water > 0 && (savings.laundry > 0 || savings.energy > 0) && ' • '}
          {savings.laundry > 0 && `${savings.laundry}kg laundry`}
          {savings.laundry > 0 && savings.energy > 0 && ' • '}
          {savings.energy > 0 && `${savings.energy}kWh energy`}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      {savings.water > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-blue-300">
          <Droplets className="w-3.5 h-3.5" />
          <span>{savings.water}L</span>
        </div>
      )}
      {savings.laundry > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-purple-300">
          <span>🧺</span>
          <span>{savings.laundry}kg</span>
        </div>
      )}
      {savings.energy > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-yellow-300">
          <Zap className="w-3.5 h-3.5" />
          <span>{savings.energy}kWh</span>
        </div>
      )}
      {savings.co2 > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-green-300">
          <Wind className="w-3.5 h-3.5" />
          <span>{savings.co2}kg CO₂</span>
        </div>
      )}
    </div>
  );
}
